/**
 * Session Manager
 *
 * Zustand store that orchestrates session lifecycle by combining:
 * - Pure state machine (sessionReducer) for state transitions
 * - wallet-service for wallet store interactions
 * - auth-service for auth store interactions
 *
 * Does NOT replace useBrowserWallet or useAuth — existing consumers unchanged.
 */

import { create } from "zustand";

import { sessionReducer, INITIAL_STATE } from "./state-machine";
import type {
  SessionState,
  SessionEvent,
  SessionWalletOps,
  PreviousWallet,
} from "./types";
import * as walletService from "./services/wallet-service";
import * as authService from "./services/auth-service";

/**
 * Actions that touch the wallet device or sign take a `SessionWalletOps`
 * parameter — pass `useBrowserWallet()`'s return value (structurally
 * assignable). The provider-owned wallet core is the only holder of the
 * Ledger transport / signing keys, so these capabilities are injected
 * from the React layer instead of imported here.
 */
interface SessionActions {
  /** Dispatch a raw event through the state machine (internal use) */
  dispatch: (event: SessionEvent) => void;

  /** Restore session from persisted stores on app mount */
  restore: () => void;

  /** Connect a wallet and begin authentication */
  connect: (address: string, wallet: SessionWalletOps) => Promise<void>;

  /** User confirmed signing — execute sign + verify flow */
  confirmSign: (wallet: SessionWalletOps) => Promise<void>;

  /** Retry after auth error */
  retry: () => Promise<void>;

  /** Cancel authentication (or wallet switch) */
  cancel: (wallet: SessionWalletOps) => void;

  /** Switch to a different wallet while authenticated */
  switchWallet: (address: string, wallet: SessionWalletOps) => Promise<void>;

  /** Reconnect ledger device */
  reconnect: (wallet: SessionWalletOps) => Promise<void>;

  /** Logout — invalidate JWT server-side + clear state */
  logout: (wallet: SessionWalletOps) => Promise<void>;

  /** Disconnect — clear state without server-side logout */
  disconnect: (wallet: SessionWalletOps) => void;
}

interface SessionManagerState extends SessionState, SessionActions {
  _expiryTimer: ReturnType<typeof setTimeout> | null;
  _previousWallet: PreviousWallet | null;
}

function extractState(store: SessionManagerState): SessionState {
  return {
    status: store.status,
    walletAddress: store.walletAddress,
    walletType: store.walletType,
    walletId: store.walletId,
    authSubState: store.authSubState,
    challengeMessage: store.challengeMessage,
    blockhash: store.blockhash,
    authError: store.authError,
    reconnectError: store.reconnectError,
    previousStatus: store.previousStatus,
  };
}

export const useSessionManager = create<SessionManagerState>()((set, get) => {
  // ─── Internal helpers ──────────────────────────────────────────

  function dispatch(event: SessionEvent): void {
    const currentState = extractState(get());
    const nextState = sessionReducer(currentState, event);
    set(nextState);
  }

  function clearExpiryTimer(): void {
    const timer = get()._expiryTimer;
    if (timer) {
      clearTimeout(timer);
      set({ _expiryTimer: null });
    }
  }

  function scheduleExpiry(): void {
    clearExpiryTimer();
    const expiresAt = authService.getExpiresAt();
    if (!expiresAt) return;

    const msUntilExpiry = expiresAt - Date.now();
    if (msUntilExpiry <= 0) {
      dispatch({ type: "JWT_EXPIRED" });
      walletService.lockWallet();
      return;
    }

    const timer = setTimeout(() => {
      dispatch({ type: "JWT_EXPIRED" });
      authService.clearAuth();
      walletService.lockWallet();
    }, msUntilExpiry);

    set({ _expiryTimer: timer });
  }

  async function runChallengeFlow(address: string): Promise<void> {
    try {
      const challenge = await authService.getChallenge(address);
      dispatch({
        type: "CHALLENGE_RECEIVED",
        message: challenge.message,
        blockhash: challenge.blockhash,
      });
    } catch (error) {
      dispatch({
        type: "CHALLENGE_FAILED",
        error: error instanceof Error ? error.message : "Challenge failed",
      });
    }
  }

  // ─── Auth store subscription (401 sync) ─────────────────────────

  // Subscribe to auth store changes — when API client calls clearAuth()
  // on 401, SessionManager reacts by transitioning to idle.
  authService.subscribeToAuthChanges((isAuthenticated, wasAuthenticated) => {
    if (wasAuthenticated && !isAuthenticated) {
      const currentStatus = get().status;
      if (
        currentStatus === "active" ||
        currentStatus === "wallet_disconnected"
      ) {
        clearExpiryTimer();
        walletService.lockWallet();
        dispatch({ type: "JWT_EXPIRED" });
      }
    }
  });

  // ─── Store definition ──────────────────────────────────────────

  return {
    ...INITIAL_STATE,
    _expiryTimer: null,
    _previousWallet: null,

    dispatch,

    restore: () => {
      dispatch({ type: "RESTORE_START" });

      const persistedAuth = authService.getPersistedAuth();
      const persistedWallet = walletService.getPersistedWallet();

      // No persisted session — go to idle
      if (
        !persistedAuth.isAuthenticated ||
        !persistedAuth.wallet ||
        !persistedAuth.token ||
        !persistedWallet
      ) {
        // Clean up any stale state
        if (persistedAuth.isAuthenticated) authService.clearAuth();
        dispatch({ type: "RESTORE_NO_SESSION" });
        return;
      }

      // Wallet address mismatch — session invalid
      if (persistedAuth.wallet !== persistedWallet.address) {
        authService.clearAuth();
        dispatch({ type: "RESTORE_NO_SESSION" });
        return;
      }

      // JWT expired
      if (authService.isTokenExpired()) {
        authService.clearAuth();
        dispatch({ type: "RESTORE_EXPIRED" });
        return;
      }

      const walletType = persistedWallet.walletType;

      if (walletType === "hot") {
        // Hot wallet: unlock immediately, no device needed
        walletService.unlockWallet(
          persistedWallet.address,
          persistedWallet.walletId!,
          persistedWallet.walletName,
          "hot",
        );
        dispatch({
          type: "RESTORE_HOT_WALLET",
          walletAddress: persistedWallet.address,
          walletId: persistedWallet.walletId!,
        });
        scheduleExpiry();
      } else if (walletType === "ledger") {
        // Ledger: JWT valid but device not connected
        dispatch({
          type: "RESTORE_LEDGER",
          walletAddress: persistedWallet.address,
          walletId: persistedWallet.walletId!,
        });
        scheduleExpiry();
      } else {
        // Unknown wallet type
        authService.clearAuth();
        dispatch({ type: "RESTORE_NO_SESSION" });
      }
    },

    connect: async (address: string, wallet: SessionWalletOps) => {
      try {
        const { type, walletId } = await walletService.connectWallet(
          address,
          wallet.selectWallet,
        );
        dispatch({
          type: "CONNECT",
          walletAddress: address,
          walletType: type,
          walletId,
        });

        // Immediately start challenge flow
        await runChallengeFlow(address);
      } catch (error) {
        // selectWallet already cleaned up wallet store on error
        dispatch({
          type: "CHALLENGE_FAILED",
          error:
            error instanceof Error ? error.message : "Failed to connect wallet",
        });
      }
    },

    confirmSign: async (wallet: SessionWalletOps) => {
      const state = get();
      if (
        state.status !== "authenticating" ||
        !state.walletAddress ||
        !state.walletType ||
        !state.challengeMessage
      ) {
        return;
      }

      dispatch({ type: "USER_CONFIRM_SIGN" });

      try {
        // Sign the challenge message via the package hook's signMessage —
        // it routes hot vs Ledger internally (incl. the self-transfer
        // fallback, which consumes the blockhash) and updates last-used
        // bookkeeping for Ledger wallets.
        const messageBytes = new TextEncoder().encode(state.challengeMessage);
        const signResult = await wallet.signMessage(
          messageBytes,
          state.blockhash ?? undefined,
        );

        dispatch({ type: "SIGNATURE_OBTAINED" });

        // Verify signature with backend
        const authResult = await authService.verifySignature(
          state.walletAddress,
          state.challengeMessage,
          signResult,
        );

        // Set authenticated in auth store
        authService.setAuthenticated(
          authResult.wallet,
          authResult.token,
          authResult.jti,
          authResult.sid,
          authResult.expiresIn,
        );

        dispatch({ type: "AUTH_SUCCESS" });
        scheduleExpiry();
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Authentication failed";

        // If this was a wallet switch, roll back
        const previousWallet = get()._previousWallet;
        if (state.previousStatus === "active" && previousWallet) {
          try {
            await walletService.connectWallet(
              previousWallet.address,
              wallet.selectWallet,
            );
          } catch {
            // If rollback also fails, we still go to SWITCH_FAILED
          }
          dispatch({ type: "SWITCH_FAILED", previousWallet });
          set({ _previousWallet: null });
          return;
        }

        dispatch({ type: "AUTH_ERROR", error: errorMessage });
      }
    },

    retry: async () => {
      const state = get();
      if (state.status !== "authenticating" || !state.walletAddress) return;

      dispatch({ type: "AUTH_RETRY" });
      await runChallengeFlow(state.walletAddress);
    },

    cancel: (wallet: SessionWalletOps) => {
      const state = get();
      const previousWallet = get()._previousWallet;

      if (state.previousStatus === "active" && previousWallet) {
        // Reconnect old wallet on cancel
        walletService
          .connectWallet(previousWallet.address, wallet.selectWallet)
          .catch(() => {});
        dispatch({ type: "AUTH_CANCEL", previousWallet });
      } else {
        wallet.disconnect();
        dispatch({ type: "AUTH_CANCEL" });
      }
      set({ _previousWallet: null });
    },

    switchWallet: async (address: string, wallet: SessionWalletOps) => {
      const state = get();
      if (state.status !== "active") return;

      // Save current wallet for rollback
      const previousWallet: PreviousWallet = {
        address: state.walletAddress!,
        type: state.walletType!,
        id: state.walletId!,
      };
      set({ _previousWallet: previousWallet });

      try {
        const { type, walletId } = await walletService.connectWallet(
          address,
          wallet.selectWallet,
        );
        dispatch({
          type: "SWITCH_WALLET",
          walletAddress: address,
          walletType: type,
          walletId,
        });

        await runChallengeFlow(address);
      } catch {
        // Switch failed during wallet connect — roll back
        try {
          await walletService.connectWallet(
            previousWallet.address,
            wallet.selectWallet,
          );
        } catch {
          // Rollback failed too
        }
        set({ _previousWallet: null });
        // Stay in active state (SWITCH_FAILED handled via dispatch won't work
        // because we haven't left active yet — connectWallet failed before dispatch)
      }
    },

    reconnect: async (wallet: SessionWalletOps) => {
      const state = get();
      if (state.status !== "wallet_disconnected" || !state.walletAddress) {
        return;
      }

      try {
        await walletService.connectWallet(
          state.walletAddress,
          wallet.selectWallet,
        );
        dispatch({ type: "RECONNECT_SUCCESS" });
      } catch (error) {
        dispatch({
          type: "RECONNECT_DEVICE_ERROR",
          error: error instanceof Error ? error.message : "Reconnect failed",
        });
      }
    },

    logout: async (wallet: SessionWalletOps) => {
      clearExpiryTimer();
      const token = authService.getToken();
      if (token) {
        await authService.logoutServer(token);
      }
      authService.clearAuth();
      wallet.disconnect();
      dispatch({ type: "LOGOUT" });
    },

    disconnect: (wallet: SessionWalletOps) => {
      clearExpiryTimer();
      authService.clearAuth();
      wallet.disconnect();
      dispatch({ type: "DISCONNECT" });
    },
  };
});
